import { useState, useMemo, useCallback } from 'react';
import { cva } from 'class-variance-authority';
import { motion } from 'motion/react';
import * as Sentry from '@sentry/react';
import {
  Flex,
  Text,
  Button,
  IconButton,
  Tag,
  Dropdown,
  Pagination,
  PageCard,
  space,
  iconWeight,
  Icon,
  useReducedMotion,
  tableActionsCell,
  notification,
} from '@nula/core-ui';
import { useNavigateToEntity } from '@/hooks/navigation';
import { Library } from '@/components/document-cards/library';

import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { formatDate } from '@/utils/format-date';
import { useWorkspaceCollections } from '@/lib/sync/hooks/use-workspace-collections';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { isSharedWithMe, canRemoveSelf, canDelete, type Access } from '@/lib/sharing/utils/permissions';
import { type AccessFilter } from '@/components/document-cards/library';
import { SharedByChip } from '@/lib/sharing/components/shared-by-chip';
import { useGatedActions } from '@/lib/sharing/hooks/use-gated-actions';
import { ConfirmMenuItem } from '@/components/confirm-menu-item/confirm-menu-item';
import { CollectionPreviewStack } from './collection-preview-stack';
import { SelectionActions } from '@/components/document-cards/selection-actions';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import styles from './collection-library.module.css';
import tableStyles from '@/components/document-cards/library-table.module.css';

// ── CVA ──────────────────────────────────────────────────────────────────────

const listCard = cva(styles.listCard);

// ── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 12;

// ── Collection card ──────────────────────────────────────────────────────────

interface CollectionCardData {
  id: string;
  name: string;
  itemCount: number;
  tags: string[];
  access: Access;
}

interface CollectionCardProps {
  collection: CollectionCardData;
  onOpen: (id: string, name: string) => void;
  onArchive: (id: string) => void;
  listView?: boolean;
}

function removeSelfConfirmSentence(name: string, itemCount: number | null): React.ReactNode {
  if (!itemCount) {
    return <>Remove <Text strong>{name}</Text> from your workspace?</>;
  }
  return (
    <>Remove <Text strong>{name}</Text> and the {itemCount} item{itemCount === 1 ? '' : 's'} inside it from your workspace?</>
  );
}

export function CollectionCard({ collection, onOpen, onArchive, listView, checkboxNode }: CollectionCardProps & { checkboxNode?: React.ReactNode }) {
  const shouldReduce = useReducedMotion();
  const service = useWorkspaceService();

  // Gate destructive actions by the user's permission on this collection.
  const gatedCollectionActions = useGatedActions(collection.id, [
    { key: 'trash', require: 'delete' as const, label: 'Move to Trash', icon: <Icon name="delete" size={14} weight={iconWeight.light} />, danger: true, onClick: () => onArchive(collection.id) },
    {
      key: 'remove-self',
      require: 'removeSelf' as const,
      icon: <Icon name="logout" size={14} weight={iconWeight.light} />,
      onClick: () => {},
      label: (
        <ConfirmMenuItem
          label="Remove from my workspace"
          confirmLabel={removeSelfConfirmSentence(collection.name, collection.access.removeSelfItemCount)}
          onConfirm={() => service.leaveEntity(collection.id).catch((err) => Sentry.captureException(err))}
        />
      ),
    },
  ]);

  if (listView) {
    return (
      <motion.div
        whileHover={shouldReduce ? {} : { y: -space[1] }}
        onClick={() => onOpen(collection.id, collection.name)}
        className={listCard()}
      >
        <Flex
          align="center"
          gap={space[3]}
          className={styles.listCardInner}
        >
          {/* Collection icon thumbnail (neutral, no per-collection color) */}
          <Flex
            align="center"
            justify="center"
            className={styles.listThumbnail}
          >
            <Icon name={getEntityKindMeta('collection').icon} size={18} weight={iconWeight.light} className={styles.listThumbnailIcon} />
          </Flex>

          {/* Name + tags */}
          <Flex vertical className={styles.nameCol}>
            <Text
              ellipsis
              className={styles.listNameText}
            >
              {collection.name}
            </Text>
            {collection.tags.length > 0 && (
              <Flex align="center" wrap gap={space[1]} className={styles.tagsWrapRow}>
                {collection.tags.slice(0, 3).map((tag) => (
                  <Tag key={tag} bordered={false} className={styles.tag}>
                    {tag}
                  </Tag>
                ))}
              </Flex>
            )}
          </Flex>

          {/* Item count */}
          <Text className={styles.listItemCount}>
            {collection.itemCount} items
          </Text>
          <SharedByChip access={collection.access} />

          {/* More menu */}
          <Flex onClick={(e) => e.stopPropagation()} className={styles.moreBtnWrap}>
            <Dropdown
              trigger={['click']}
              placement="bottom-end"
              menu={{
                items: [
                  { key: 'open', label: 'Open', icon: <Icon name="open_in_new" size={14} weight={iconWeight.light} />, onClick: () => onOpen(collection.id, collection.name) },
                  ...(gatedCollectionActions.length > 0 ? [{ key: 'd1', label: '', type: 'divider' as const }] : []),
                  ...gatedCollectionActions,
                ],
              }}
            >
              <IconButton size="sm" aria-label="More options" className={styles.moreIconBtn}>
                <Icon name="more_horiz" size={16} weight={iconWeight.light} />
              </IconButton>
            </Dropdown>
          </Flex>
        </Flex>
      </motion.div>
    );
  }

  return (
    <PageCard
      preview={<CollectionPreviewStack collectionId={collection.id} />}
      title={collection.name}
      meta={`${collection.itemCount} ${collection.itemCount === 1 ? 'doc' : 'docs'}`}
      actions={
        <Dropdown
          trigger={['click']}
          placement="bottom-end"
          menu={{
            items: [
              {
                key: 'open',
                label: 'Open',
                icon: <Icon name="open_in_new" size={14} weight={iconWeight.light} />,
                onClick: () => onOpen(collection.id, collection.name),
              },
              ...(gatedCollectionActions.length > 0 ? [{ key: 'd1', label: '', type: 'divider' as const }] : []),
              ...gatedCollectionActions,
            ],
          }}
        >
          <IconButton size="sm" aria-label="More options">
            <Icon name="more_horiz" size={16} weight={iconWeight.light} />
          </IconButton>
        </Dropdown>
      }
      actionsLeft={checkboxNode}
      onClick={() => onOpen(collection.id, collection.name)}
    />
  );
}

// ── Main component ──────────────────────────────────────────────────────────


export function CollectionLibrary() {
  const service = useWorkspaceService();
  const collections = useWorkspaceCollections();
  const allDocuments = useWorkspaceDocs();

  const navigateToEntity = useNavigateToEntity();

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('modified');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [accessFilter, setAccessFilter] = useState<AccessFilter>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  // Enrich collections with item counts, filter out archived
  const enrichedCollections = useMemo(() => {
    return collections
      .filter((c) => !c.isArchived)
      .map((c) => ({
        id: c.id,
        name: c.name,
        itemCount: allDocuments.filter((d) => d.collectionIds?.includes(c.id) && !d.isArchived).length,
        tags: c.tags ?? [],
        access: c.access,
        updatedAt: c.updatedAt,
      }));
  }, [collections, allDocuments]);

  // ── Access-filter buckets ──────────────────────────────────────────────────
  const sharedCollections = useMemo(
    () => enrichedCollections.filter((c) => isSharedWithMe(c)),
    [enrichedCollections],
  );

  const handleAccessFilterChange = (filter: AccessFilter) => {
    setAccessFilter(filter);
    setPage(1);
  };

  // Extract unique tags from non-archived collections for filter pills
  const collectionTags = useMemo(() => {
    const tagSet = new Set<string>();
    collections.filter((c) => !c.isArchived).forEach((c) => c.tags?.forEach((t) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [collections]);

  const filteredItems = useMemo(() => {
    // Apply access filter first
    let items = accessFilter === 'shared' ? sharedCollections : enrichedCollections;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter((c) => c.name.toLowerCase().includes(q));
    }
    if (selectedTags.length > 0) {
      items = items.filter((c) => c.tags.some((t) => selectedTags.includes(t)));
    }
    if (sortBy === 'alpha') {
      items = [...items].sort((a, b) => a.name.localeCompare(b.name));
    }
    return items;
  }, [enrichedCollections, sharedCollections, accessFilter, searchQuery, selectedTags, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleBulkArchive = useCallback(() => {
    selectedRowKeys.forEach((id) => service.archiveCollection(id, true));
    const count = selectedRowKeys.length;
    setSelectedRowKeys([]);
    notification.success({ message: `Moved ${count} collection${count === 1 ? '' : 's'} to trash`, duration: 4000 });
  }, [selectedRowKeys, service]);

  const handleNewCollection = () => {
    const id = service.createCollection('Untitled Collection');
    navigateToEntity(id);
  };

  const tableColumns = useMemo(() => [
    {
      title: 'Title',
      key: 'name',
      sorter: (a: CollectionCardData & { updatedAt: string }, b: CollectionCardData & { updatedAt: string }) => a.name.localeCompare(b.name),
      render: (_: unknown, collection: CollectionCardData & { updatedAt: string }) => {
        return (
          <Flex align="center" gap={space[3]} className={tableStyles.nameCell}>
            <Flex
              align="center"
              justify="center"
              className={styles.listThumbnail}
            >
              <Icon name={getEntityKindMeta('collection').icon} size={18} weight={iconWeight.light} className={styles.listThumbnailIcon} />
            </Flex>
            <Flex vertical className={tableStyles.titleCol}>
              <Text ellipsis className={tableStyles.titleText}>{collection.name}</Text>
              {collection.tags.length > 0 && (
                <Flex align="center" wrap gap={space[1]} className={tableStyles.tagsRow}>
                  {collection.tags.slice(0, 3).map((tag) => (
                    <Tag key={tag} bordered={false} className={tableStyles.tag}>{tag}</Tag>
                  ))}
                  {collection.tags.length > 3 && <Text size="xs" className={tableStyles.tagsOverflow}>+{collection.tags.length - 3}</Text>}
                </Flex>
              )}
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: 'Items',
      key: 'itemCount',
      width: 140,
      sorter: (a: CollectionCardData & { updatedAt: string }, b: CollectionCardData & { updatedAt: string }) => a.itemCount - b.itemCount,
      render: (_: unknown, collection: CollectionCardData & { updatedAt: string }) => (
        <Text className={tableStyles.metricText}>{collection.itemCount} items</Text>
      ),
    },
    {
      title: 'Last Edited',
      key: 'updatedAt',
      width: 140,
      sorter: (a: CollectionCardData & { updatedAt: string }, b: CollectionCardData & { updatedAt: string }) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (_: unknown, collection: CollectionCardData & { updatedAt: string }) => (
        <Text className={tableStyles.dateText}>{collection.updatedAt ? formatDate(collection.updatedAt) : '—'}</Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      render: (_: unknown, collection: CollectionCardData & { updatedAt: string }) => (
        <Flex className={tableActionsCell} onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={['click']}
            placement="bottom-end"
            menu={{
              items: [
                { key: 'open', label: 'Open', icon: <Icon name="open_in_new" size={14} weight={iconWeight.light} />, onClick: () => navigateToEntity(collection.id) },
                { key: 'd1', label: '', type: 'divider' as const },
                ...(canDelete(collection.access) ? [{ key: 'trash', label: 'Move to Trash', icon: <Icon name="delete" size={14} weight={iconWeight.light} />, danger: true, onClick: () => service.archiveCollection(collection.id, true) }] : []),
                ...(canRemoveSelf(collection.access) ? [{
                  key: 'remove-self',
                  icon: <Icon name="logout" size={14} weight={iconWeight.light} />,
                  onClick: () => {},
                  label: (
                    <ConfirmMenuItem
                      label="Remove from my workspace"
                      confirmLabel={removeSelfConfirmSentence(collection.name, collection.access.removeSelfItemCount)}
                      onConfirm={() => service.leaveEntity(collection.id).catch((err) => Sentry.captureException(err))}
                    />
                  ),
                }] : []),
              ],
            }}
          >
            <IconButton size="sm" aria-label="More options" className={tableStyles.moreBtn}>
              <Icon name="more_horiz" size={16} weight={iconWeight.light} />
            </IconButton>
          </Dropdown>
        </Flex>
      ),
    },
  ], [navigateToEntity, service]);

  return (
    <Library
      title="Collections"
      heroIcon={getEntityKindMeta('collection').icon}
      items={pagedItems}
      tableColumns={tableColumns}
      onTableRow={(collection) => ({ onClick: () => navigateToEntity(collection.id as string) })}
      selectedIds={new Set(selectedRowKeys)}
      onToggleSelect={(id) => setSelectedRowKeys((prev) => prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id])}
      onSelectAll={(ids) => setSelectedRowKeys(ids)}
      onClearSelection={() => setSelectedRowKeys([])}
      tableRowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
      selectionActions={
        selectedRowKeys.length > 0 ? (
          <SelectionActions
            actions={[
              { key: 'trash', icon: 'delete', label: 'Move to Trash', onClick: handleBulkArchive, danger: true },
            ]}
          />
        ) : undefined
      }
      renderCard={(collection, _index, vm, checkboxNode) => (
        <CollectionCard
          collection={collection}
          listView={vm === 'list'}
          onOpen={(id) => navigateToEntity(id)}
          onArchive={(id) => service.archiveCollection(id, true)}
          checkboxNode={checkboxNode}
        />
      )}
      keyExtractor={(c) => c.id}
      accessFilter={accessFilter}
      onAccessFilterChange={handleAccessFilterChange}
      searchPlaceholder="Search collections..."
      onSearch={(q: string) => { setSearchQuery(q); setPage(1); }}
      emptyIllustration="collections"
      emptyTitle="No collections yet"
      emptyMessage="Your space is clear. Start grouping related documents here."
      cardLayout={viewMode === 'grid' ? 'pageGrid' : undefined}
      viewMode={viewMode}
      onViewChange={setViewMode}
      sortOptions={[
        { key: 'modified', label: 'Last Modified', icon: <Icon name="schedule" size={14} weight={iconWeight.light} /> },
        { key: 'alpha', label: 'Alphabetical', icon: <Icon name="sort_by_alpha" size={14} weight={iconWeight.light} /> },
      ]}
      activeSort={sortBy}
      onSortChange={setSortBy}
      tags={collectionTags}
      selectedTags={selectedTags}
      onTagsChange={setSelectedTags}
      actions={
        <Button
          variant="primary"
          icon={<Icon name="add" size={18} weight={iconWeight.light} />}
          onClick={handleNewCollection}
          className={styles.primaryBtn}
        >
          New Collection
        </Button>
      }
      pagination={
        filteredItems.length > PAGE_SIZE ? (
          <Flex justify="center" className={styles.paginationRow}>
            <Pagination
              current={currentPage}
              total={filteredItems.length}
              pageSize={PAGE_SIZE}
              onChange={setPage}
            />
          </Flex>
        ) : undefined
      }
    />
  );
}
